import React, { useState, useEffect } from "react";
import { X, Calendar, Search } from "lucide-react";
import { authFetch } from "../../../lib/api";

const getInitialFormData = (initialData) => ({
  assetType: initialData?.category || initialData?.assetType || "Laptop",
  brand: initialData?.brand || "",
  modelName: initialData?.modelName || "",
  serialNumber: initialData?.serialNumber || "",
  warrantyExpiry: initialData?.warrantyExpiry
    ? String(initialData.warrantyExpiry).split("T")[0]
    : "",
  assignedUserId: initialData?.assignedUserId || "",
  employeeId: initialData?.employeeId || "",
});

// Translates raw backend/DB errors into messages a non-technical user can
// actually act on. Falls back to the raw message if we don't recognize it,
// so nothing is silently swallowed.
const getFriendlyErrorMessage = (rawError) => {
  if (!rawError) return "";

  const msg = String(rawError);

  // Mongo duplicate key error, e.g.:
  // "E11000 duplicate key error collection: qwickhub.assetinventories
  //  index: serialNumber_1 dup key: { serialNumber: null }"
  if (msg.includes("E11000") || msg.toLowerCase().includes("duplicate key")) {
    if (msg.includes("serialNumber")) {
      // "dup key: { serialNumber: null }" happens when multiple assets are
      // saved with an empty serial number and the field has a unique index.
      if (msg.includes("serialNumber: null") || msg.includes('"serialNumber":null')) {
        return "Serial Number can't be left blank for this asset type — it must be unique. Please enter a serial number, or mark this asset type as one that doesn't require one.";
      }
      return "This Serial Number is already assigned to another asset. Please enter a unique serial number.";
    }
    if (msg.includes("employeeId")) {
      return "This Employee ID is already linked to another asset.";
    }
    // Generic duplicate key fallback
    return "One of the values you entered must be unique, but it's already in use. Please double-check the highlighted field(s).";
  }

  if (msg.toLowerCase().includes("validation failed")) {
    return "Some fields are missing or invalid. Please review the form and try again.";
  }

  if (msg.toLowerCase().includes("network") || msg.toLowerCase().includes("failed to fetch")) {
    return "Couldn't reach the server. Please check your connection and try again.";
  }

  // Unknown error shape — show as-is so it's still visible for debugging,
  // but this should get less common as more cases are mapped above.
  return msg;
};

const AddAssetModal = ({
  onClose,
  onSubmit,
  initialData = null,
  mode = "add",
  errorMessage = "",
}) => {
  const [formData, setFormData] = useState(() =>
    getInitialFormData(initialData),
  );
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [showUserDrop, setShowUserDrop] = useState(false);
  const [loadingEmployeeId, setLoadingEmployeeId] = useState(false);
  const [dateFocused, setDateFocused] = useState(false);

  useEffect(() => {
    setFormData(getInitialFormData(initialData));
  }, [initialData]);

  const isIdCard = formData.assetType === "ID Card";
  const isSerialOptional =
    formData.assetType === "Broadband" || formData.assetType === "Others";

  // Members are only needed for the ID Card flow
  useEffect(() => {
    if (!isIdCard) return;
    const load = async () => {
      try {
        const res = await authFetch("/api/admin/users?picker=true");
        const json = await res.json();
        if (json?.data) setUsers(json.data);
      } catch {}
    };
    load();
  }, [isIdCard]);

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(userSearch.toLowerCase()),
  );

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Builds the ID Card "serial number" as EmployeeID-Name, e.g.
  // "QB1907260067/SDE1-ZulnainPasha". This keeps every ID Card's serial
  // number unique (tied to the person) instead of being left blank, which
  // used to trip the DB's unique index and throw a duplicate-key error.
  const buildIdCardSerial = (employeeId, name) => {
    if (!employeeId || !name) return "";
    return `${employeeId}-${name.replace(/\s+/g, "")}`;
  };

  // The picker list is a lightweight response (no employeeId) — fetch the
  // full profile for the chosen user so their real Employee ID shows up.
  const handleSelectUser = async (user) => {
    setFormData((f) => ({
      ...f,
      assignedUserId: user._id,
      employeeId: "",
      serialNumber: "",
    }));
    setShowUserDrop(false);
    setUserSearch("");

    setLoadingEmployeeId(true);
    try {
      const res = await authFetch(`/api/admin/users/${user._id}`);
      const json = await res.json();
      const employeeId = json?.data?.employeeId || "";
      setFormData((f) =>
        f.assignedUserId === user._id
          ? {
              ...f,
              employeeId,
              serialNumber: buildIdCardSerial(employeeId, user.name),
            }
          : f,
      );
    } catch {
    } finally {
      setLoadingEmployeeId(false);
    }
  };

  const handleSubmit = async () => {
    await onSubmit(formData);
  };

  const isEditMode = mode === "edit";
  const friendlyError = getFriendlyErrorMessage(errorMessage);

  // Shared classes so every label / input in the form look identical,
  // matching the reference design (same size + weight throughout).
  const labelClass = "text-[16px] font-normal text-black";
  const inputClass =
    "mt-2 w-full h-10 border border-black/[0.19] rounded-lg px-3 text-[14px] outline-none placeholder:text-black/40";

  return (
    <div className="fixed inset-0 bg-black/30 z-[2000] flex items-center justify-center">
      {/* Modal */}
      <div className="w-[668px] max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-xl px-8 py-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-[24px] font-semibold text-black">
              {isEditMode ? "Edit Asset" : "Add Asset"}
            </h2>

            <p className="text-[14px] text-black/50 mt-1">
              {isEditMode
                ? "Update the selected asset details below."
                : "Register a new asset to the company inventory."}
            </p>
          </div>

          <button onClick={onClose} className="text-gray-400 hover:text-black">
            <X size={18} />
          </button>
        </div>

        <hr className="my-4" />

        {/* Form */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
          {/* Asset Type */}
          <div>
            <label className={labelClass}>Asset Type</label>

            <select
              name="assetType"
              value={formData.assetType}
              onChange={handleChange}
              className={inputClass}
            >
              <option>Laptop</option>
              <option>Mobile</option>
              <option>Headset</option>
              <option>ID Card</option>
              <option>Broadband</option>
              <option>Others</option>
            </select>
          </div>

          {isIdCard ? (
            <>
              {/* Asset — the ID Card's serial number, auto-built as
                  EmployeeID-Name once a member is selected below */}
              <div>
                <label className={labelClass}>Asset</label>

                <input
                  readOnly
                  value={
                    loadingEmployeeId
                      ? "Loading..."
                      : formData.serialNumber || "Select a member below"
                  }
                  className={`${inputClass} bg-white text-black`}
                />
              </div>

              {/* Assign To — member picker driving the Employee ID above */}
              <div className="col-span-2">
                <label className={labelClass}>Assign To</label>

                <div className="relative mt-2">
                  <button
                    type="button"
                    onClick={() => setShowUserDrop((v) => !v)}
                    className="w-full h-10 border border-black/[0.19] rounded-lg px-3 text-[14px] text-left flex items-center justify-between bg-white"
                  >
                    <span
                      className={
                        formData.assignedUserId
                          ? "text-gray-800"
                          : "text-gray-400"
                      }
                    >
                      {users.find((u) => u._id === formData.assignedUserId)
                        ?.name || "Select a member"}
                    </span>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#9ca3af"
                      strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>

                  {showUserDrop && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowUserDrop(false)}
                      />
                      <div className="absolute top-full mt-1 left-0 w-full bg-white border border-gray-200 rounded-md shadow-lg z-20 overflow-hidden">
                        <div className="flex items-center gap-2 border-b px-3 py-2">
                          <Search size={13} className="text-gray-400" />
                          <input
                            autoFocus
                            type="text"
                            placeholder="Search Members"
                            className="text-xs outline-none flex-1 bg-transparent"
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                          />
                        </div>
                        <div className="max-h-40 overflow-y-auto">
                          {filteredUsers.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-3">
                              No users found
                            </p>
                          ) : (
                            filteredUsers.map((u) => (
                              <button
                                key={u._id}
                                type="button"
                                onClick={() => handleSelectUser(u)}
                                className="flex items-center gap-3 w-full text-left px-3 py-2 hover:bg-gray-50"
                              >
                                <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-blue-600 flex-shrink-0">
                                  {u.name[0].toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs font-medium text-gray-800 truncate">
                                    {u.name}
                                  </p>
                                  <p className="text-[10px] text-gray-400 truncate">
                                    {u.email}
                                  </p>
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Brand */}
              <div>
                <label className={labelClass}>Brand</label>

                <input
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  placeholder="Eg: Dell, Samsung"
                  className={inputClass}
                />
              </div>

              {/* Model Name */}
              <div>
                <label className={labelClass}>Model Name</label>

                <input
                  name="modelName"
                  value={formData.modelName}
                  onChange={handleChange}
                  placeholder="Eg: Latitude 5420"
                  className={inputClass}
                />
              </div>

              {/* Serial Number */}
              <div>
                <label className={labelClass}>
                  Serial Number{isSerialOptional ? " (optional)" : ""}
                </label>

                <input
                  name="serialNumber"
                  value={formData.serialNumber}
                  onChange={handleChange}
                  placeholder={isSerialOptional ? "Optional" : "Eg: DL-89X12"}
                  className={inputClass}
                />
              </div>

              {/* Warranty */}
              <div>
                <label className={labelClass}>Warranty Expiry</label>

                <div className="relative mt-2 h-10 border border-black/[0.19] rounded-lg flex items-center overflow-hidden">
                  <div className="w-[67px] h-10 bg-black/[0.04] flex items-center justify-center flex-shrink-0">
                    <Calendar size={20} className="text-[#3B82F6]" />
                  </div>
                  <div className="relative flex-1 h-full">
                    <input
                      type="date"
                      name="warrantyExpiry"
                      value={formData.warrantyExpiry}
                      onChange={handleChange}
                      onFocus={() => setDateFocused(true)}
                      onBlur={() => setDateFocused(false)}
                      className={`absolute inset-0 w-full h-full px-3 text-[14px] outline-none border-0 bg-transparent [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer ${
                        formData.warrantyExpiry || dateFocused
                          ? "opacity-100 text-black"
                          : "opacity-0"
                      }`}
                    />
                    {!formData.warrantyExpiry && !dateFocused && (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-black/30 pointer-events-none">
                        Enter the expiry date
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Status — always Available for a newly registered asset */}
          <div className={isIdCard ? "col-span-2" : ""}>
            <label className={labelClass}>Status</label>

            <input
              readOnly
              value="Available"
              className={`${inputClass} bg-white text-black`}
            />
          </div>
        </div>

        {friendlyError ? (
          <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-600">
            {friendlyError}
          </p>
        ) : null}

        {/* Footer */}
        <div className="flex justify-end gap-2 mt-8">
          <button
            onClick={onClose}
            className="w-[163px] h-10 border border-[#4253C7] rounded-lg text-[#4253C7] text-[14px] hover:bg-[#4253C7] hover:text-white transition"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            className="w-[163px] h-10 bg-[#4253C7] rounded-lg text-white text-[14px] hover:opacity-90 transition"
          >
            {isEditMode ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddAssetModal;